<template>
  <v-dialog v-model="show" max-width="800">
    <v-card>
      <v-card-title class="body-1">
        Client: {{ client ? client.PhoneNumber : '' }} (ID = {{ clientId }})
        <v-btn
          outlined
          small
          :class="$vuetify.breakpoint.smAndUp ? 'ml-3' : ''"
          @click="sendNotificationDialog = true"
        >
          <v-icon small>mdi-cellphone-android</v-icon>Send notification
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn icon small @click="show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text v-if="client">
        <v-row>
          <v-col cols="12" sm="6">
            <v-row>
              <v-col cols="6">
                <dl>
                  <div class="font-weight-bold secondary--text">
                    Name
                  </div>
                  <dd>{{ client.Name || '-' }}</dd>
                  <div class="font-weight-bold secondary--text">
                    Phone number
                  </div>
                  <dd>
                    {{ client.PhoneNumber }}
                    <span
                      v-if="client.Operator"
                      :title="`Updated at ${client.OperatorTime}`"
                    >
                      ({{ client.Operator }})
                    </span>
                  </dd>
                  <div class="font-weight-bold secondary--text">
                    Area code
                  </div>
                  <dd>{{ client.AreaCode }} ({{ client.Region }})</dd>
                  <div class="font-weight-bold secondary--text">
                    Email
                  </div>
                  <dd>{{ client.Email || '-' }}</dd>
                  <div class="font-weight-bold secondary--text">
                    Language
                  </div>
                  <dd>{{ client.Language }}</dd>
                  <div class="font-weight-bold secondary--text">
                    Registered
                  </div>
                  <dd>{{ dateEE(client.CreatedAt) }}</dd>
                </dl>
              </v-col>
              <v-col cols="6">
                <div class="font-weight-bold secondary--text">
                  Payment method
                </div>
                <dd>{{ client.PaymentMethod }}</dd>
                <div class="font-weight-bold secondary--text">
                  Mobile payments
                </div>
                <v-icon v-if="client.MobilePaymentsAvailable" color="success">
                  mdi-check
                </v-icon>
                <v-icon v-else color="error">mdi-close</v-icon>
                <div class="font-weight-bold secondary--text">
                  Creditcard payments
                </div>
                <v-icon
                  v-if="client.CreditCardPaymentsAvailable"
                  color="success"
                >
                  mdi-check
                </v-icon>
                <v-icon v-else color="error">mdi-close</v-icon>
                <div class="font-weight-bold secondary--text">
                  Summarized invoices
                </div>
                <v-icon v-if="client.SummaryInvoices" color="success">
                  mdi-check
                </v-icon>
                <v-icon v-else color="error">mdi-close</v-icon>
                <div class="font-weight-bold secondary--text">
                  Bonus credit
                </div>
                <dd>
                  {{ client.BonusCreditCents / 100.0 }}
                  {{ client.BonusCreditCurrency }}
                </dd>
                <div class="font-weight-bold secondary--text">
                  No debt
                  <i
                    class="text-default glyphicon glyphicon-info-sign"
                    @click="showDebtDetails = !showDebtDetails"
                  ></i>
                </div>
                <span v-show="showDebtDetails">
                  <dd v-if="client.Debts.UnpaidSubscriptionsCount > 0">
                    {{ client.Debts.UnpaidSubscriptionsCount }} monthly pass
                    subscriptions unpaid for more than a month.
                  </dd>
                  <dd
                    v-for="(currency, cents) in client.Debts.Amounts"
                    :key="cents"
                  >
                    {{ cents / 100 }} {{ currency }} owed.
                  </dd>
                </span>
                <v-icon v-if="!client.HasDebt" color="success">
                  mdi-check
                </v-icon>
                <v-icon v-else color="error">mdi-close</v-icon>
                <dt>
                  <button
                    class="btn btn-sm btn-default"
                    type="button"
                    @click="payDebts(clientId)"
                  >
                    Pay debts
                  </button>
                </dt>
                <div class="font-weight-bold secondary--text">
                  Created by modem
                </div>
                <v-icon v-if="client.CreatedByModem" color="success">
                  mdi-check
                </v-icon>
                <v-icon v-else color="error">mdi-close</v-icon>
                <div class="font-weight-bold secondary--text">
                  Wants notifications
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-if="client.WantsNotifications"
                      color="success"
                      v-on="on"
                    >
                      mdi-check
                    </v-icon>
                    <v-icon v-else color="error" v-on="on">mdi-close</v-icon>
                  </template>
                  <span>{{
                    `Push available: ${client.PushNotificationsAvailable}`
                  }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <div class="font-weight-bold secondary--text">
                  Highlight
                </div>
                <input
                  v-model="client.Highlight"
                  type="color"
                  class="form-control"
                  name="Highlight"
                  placeholder="Highlight"
                />
              </v-col>
              <v-col cols="6">
                <v-checkbox
                  v-model="client.BlackListed"
                  label="Blacklisted"
                  hide-details
                  class="ma-0"
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="client.Comment"
                  :label="$vuetify.lang.t('$vuetify.COMMENTS')"
                  hide-details
                  outlined
                >
                </v-textarea>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <dl>
                  <div class="font-weight-bold secondary--text">
                    Notifications:
                  </div>
                  <div
                    v-for="notification in client.RecentNotifications"
                    :key="notification.Id"
                    style="background-color: lightgray;"
                    class="mb-2 pa-2 rounded"
                  >
                    <strong>
                      {{ notification.NotificationType }} sent by
                      {{ notification.SentBy }} at
                      {{ dateEE(notification.CreatedAt) }}</strong
                    >
                    <div v-if="notification.Title">
                      {{ notification.Title }}
                    </div>
                    <div>
                      {{ notification.Message }}
                    </div>
                    {{ client.RecentNotifications.length }}
                  </div>
                  <div v-if="client.RecentNotifications.length <= 0">
                    No notifications
                  </div>
                </dl>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="font-weight-bold secondary--text">
              Monthly passes:
            </div>
            <div
              v-for="mp in client.MonthlyPasses"
              :key="mp.Id"
              class="rounded mb-2 pa-2"
              style="background-color: lightgray;"
              :title="
                `${mp.IsSubscription ? 'Subscription, ' : ''}monthly cost ${
                  mp.MonthlyCostInCents
                }, paid until ${mp.PaidUntil}`
              "
            >
              {{ dateEE(mp.ValidFrom) }} -
              {{
                mp.ValidTo !== '0001-01-01T00:00:00Z'
                  ? dateEE(mp.ValidTo)
                  : '...'
              }}
              {{ mp.PlaceDescription }}
            </div>
            <dd v-if="client.MonthlyPasses.length == 0">-</dd>

            <div class="font-weight-bold secondary--text">
              Valid credits:
            </div>
            <div
              v-for="(cp, i) in client.ValidCredits"
              :key="i"
              class="rounded mb-2 pa-2"
              style="background-color: lightgray;"
            >
              {{ cp.AvailableCents / 100.0 }} {{ cp.Currency
              }}{{ cp.Location ? ' in ' + cp.Location : ''
              }}{{ cp.ValidTo ? ' until ' + cp.ValidTo : ''
              }}{{ cp.Shared ? ' (shared)' : '' }}
            </div>
            <dd v-if="client.ValidCredits.length == 0">-</dd>

            <div class="font-weight-bold secondary--text">
              Last 10 sessions:
            </div>
            <div
              v-for="s in client.Last10Sessions"
              :key="s.Id"
              class="rounded mb-2 pa-2"
              style="background-color: lightgray;"
              :title="`Payment status: ${s.PaymentStatus}`"
            >
              {{ s.CarNumber }}
              {{ dateEE(s.StartTime) }} -
              {{
                s.EndTime !== '0001-01-01T00:00:00Z' ? dateEE(s.EndTime) : '...'
              }}
              {{ s.PlaceDescription }}
              <!-- <span
                v-show="s.PaymentStatus != ''"
                class="label"
                :class="{
                  'label-default':
                    s.PaymentStatus == 'NOT_SENT' || s.PaymentStatus == 'TEST',
                  'label-success': s.PaymentStatus == 'OK',
                  'label-danger': s.PaymentStatus == 'ERROR',
                  'label-warning':
                    s.PaymentStatus == 'PARTIALLY_REFUNDED' ||
                    s.PaymentStatus == 'TOO_BIG',
                  'label-info': s.PaymentStatus == 'REFUNDED',
                  'label-primary': s.PaymentStatus == 'PENDING'
                }"
              >
                {{ s.PaymentStatus }}
              </span> -->
              <span
                v-show="s.PaymentStatus != ''"
                left
                :class="badgeColor(s)"
                class="white--text font-weight-bold caption"
                style="border-radius: 20px; padding: 4px 6px;"
              >
                {{ s.PaymentStatus }}
              </span>
            </div>
            <dd v-if="client.Last10Sessions.length == 0">-</dd>

            <div class="font-weight-bold secondary--text">
              Recent errors:
            </div>
            <div
              v-for="(error, i) in client.RecentErrors"
              :key="i"
              class="rounded mb-2 pa-2"
              style="background-color: lightgray;"
            >
              <app-icon :app-name="error.AppName"></app-icon>
              {{ dateEE(error.CreatedAt) }}
              {{ error.Source }} - {{ error.Message }}
            </div>
            <dd v-if="client.RecentErrors.length == 0">No errors</dd>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-else>
        <v-toolbar class="elevation-0 mt-12">
          <v-spacer></v-spacer>
          <v-progress-circular
            v-if="!clientLoaded"
            indeterminate
            color="secondary"
          ></v-progress-circular>
          <!-- <div v-else>
          <span v-if="!client">No data</span>
        </div> -->
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="secondary--text"
          @click="postClient(client)"
        >
          {{ $vuetify.lang.t('$vuetify.SAVE') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <send-notification-dialog
      v-if="clientId && sendNotificationDialog"
      :client-id="clientId"
      :visible="sendNotificationDialog"
      @close="sendNotificationDialog = false"
    />
  </v-dialog>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import formatDate from '@/mixins/formatDate'

export default {
  name: 'ClientDialog',
  components: {
    SendNotificationDialog: () =>
      import(
        /* webpackChunkName: "SendNotificationDialog" */ '@/components/SendNotificationDialog'
      ),
    AppIcon: () =>
      import(
        /* webpackChunkName: "AppIcon" */ '@/components/commonComponents/AppIcon'
      )
  },
  mixins: [formatDate],
  props: {
    visible: {
      type: Boolean,
      required: true,
      default: false
    },
    clientId: {
      required: true,
      default: false
    }
  },
  data() {
    return {
      client: null,
      showDebtDetails: false,
      clientLoaded: false,
      sendNotificationDialog: false
    }
  },
  computed: {
    ...mapGetters('authentication', ['user']),
    show: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value) {
          this.client = null
          this.clientLoaded = false
          this.$emit('close')
        }
      }
    }
  },
  mounted() {
    if (!this.client) {
      this.getClient(this.clientId).then(response => {
        this.client = response
        this.clientLoaded = true
      })
    }
  },
  methods: {
    ...mapActions('users', ['getClient', 'postClient', 'payDebts']),
    badgeColor(session) {
      let color = ''
      if (
        session.PaymentStatus === 'NOT_SENT' ||
        session.PaymentStatus === 'TEST'
      ) {
        color = 'secondary lighten-4'
      }
      if (session.PaymentStatus === 'OK') {
        color = 'success'
      }
      if (
        session.PaymentStatus === 'ERROR' ||
        session.PaymentStatus === 'REFUND_FAILED' ||
        session.PaymentStatus === 'PARTIAL_REFUND_FAILED'
      ) {
        color = 'error'
      }
      if (session.PaymentStatus === 'TOO_BIG') {
        color = 'warning'
      }
      if (
        session.PaymentStatus === 'REFUNDED' ||
        session.PaymentStatus === 'PARTIAL_REFUND_FAILED' ||
        session.PaymentStatus === 'REFUND_REQUESTED' ||
        session.PaymentStatus === 'PARTIAL_REFUND_REQUESTED'
      ) {
        color = 'blue lighten-2'
      }
      if (session.PaymentStatus === 'PENDING') {
        color = 'blue lighten-4'
      }

      return color
    }
  }
}
</script>
